import { useState } from 'react';
import {
  X,
  Shield,
  Key,
  Settings,
  ChevronRight,
  CheckCircle,
  Loader,
} from 'lucide-react';
import { apiClient } from '@/api/axios';
import { useTranslation } from 'react-i18next';

interface DIDManagementProps {
  onClose: () => void;
  onSave?: () => void;
  petData: {
    id: string;
    name: string;
    species: string;
    breed: string;
    did?: string;
    didStatus?: 'active' | 'revoked';
    // DID Permissions
    didPermissionsViewData?: string[];
    didPermissionsAmendFields?: string[];
    didPermissionsIssueCredentials?: string[];
    didPermissionsTimeBound?: boolean;
    didPermissionsExpiryDate?: string;
  };
}

type ManagementView = 'menu' | 'permissions';

interface PermissionSettings {
  viewData: string[];
  amendFields: string[];
  issueCredentials: string[];
  timeBound: boolean;
  expiryDate: string;
}

export function DIDManagement({
  onClose,
  onSave,
  petData,
}: DIDManagementProps) {
  const { i18n } = useTranslation();
  const isItalian = (i18n.resolvedLanguage || i18n.language || '')
    .toLowerCase()
    .startsWith('it');
  const tr = (en: string, it: string) => (isItalian ? it : en);
  const [currentView, setCurrentView] = useState<ManagementView>('menu');
  const [isSaving, setIsSaving] = useState(false);
  const [saveSuccess, setSaveSuccess] = useState(false);
  // Initialize with saved permissions from petData, or use defaults if not set
  const [permissions, setPermissions] = useState<PermissionSettings>({
    viewData: petData.didPermissionsViewData ?? ['veterinarians', 'groomers'],
    amendFields: petData.didPermissionsAmendFields ?? ['veterinarians'],
    issueCredentials: petData.didPermissionsIssueCredentials ?? [
      'veterinarians',
    ],
    timeBound: petData.didPermissionsTimeBound ?? false,
    expiryDate: petData.didPermissionsExpiryDate ?? '',
  });

  const handleSavePermissions = async () => {
    setIsSaving(true);
    setSaveSuccess(false);

    try {
      await apiClient.put(`/pets/${petData.id}/did/permissions`, {
        viewData: permissions.viewData,
        amendFields: permissions.amendFields,
        issueCredentials: permissions.issueCredentials,
        timeBound: permissions.timeBound,
        expiryDate: permissions.timeBound ? permissions.expiryDate : null,
      });

      setSaveSuccess(true);
      // Refresh pet data to update local state
      if (onSave) {
        onSave();
      }
      // Show success briefly then close
      setTimeout(() => {
        onClose();
      }, 1500);
    } catch (error) {
      console.error('Failed to save permissions:', error);
      alert(
        tr(
          'Failed to save permissions. Please try again.',
          'Salvataggio permessi non riuscito. Riprova.'
        )
      );
    } finally {
      setIsSaving(false);
    }
  };

  const professionalRoles = [
    {
      id: 'veterinarians',
      label: tr('Veterinarians', 'Veterinari'),
      icon: '🏥',
    },
    { id: 'groomers', label: tr('Groomers', 'Toelettatori'), icon: '✂️' },
    { id: 'trainers', label: tr('Trainers', 'Addestratori'), icon: '🎓' },
    {
      id: 'boarders',
      label: tr('Boarding Facilities', 'Strutture di pensione'),
      icon: '🏠',
    },
    {
      id: 'emergency',
      label: tr('Emergency Services', 'Servizi di emergenza'),
      icon: '🚨',
    },
  ];

  const togglePermission = (type: keyof PermissionSettings, role: string) => {
    setPermissions((prev: PermissionSettings) => {
      const current = prev[type] as string[];
      if (current.includes(role)) {
        return { ...prev, [type]: current.filter((r: string) => r !== role) };
      } else {
        return { ...prev, [type]: [...current, role] };
      }
    });
  };

  // Main Menu View
  if (currentView === 'menu') {
    return (
      <>
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white dark:bg-[#16151D] rounded-xl shadow-2xl max-w-lg w-full overflow-hidden">
            {/* Header */}
            <div className="bg-gradient-to-r from-purple-600 to-indigo-600 text-white p-6">
              <div className="flex justify-between items-center">
                <div className="flex items-center gap-3">
                  <Shield className="w-8 h-8" />
                  <div>
                    <h2 className="text-xl font-semibold">
                      {tr('Manage DID', 'Gestisci DID')}
                    </h2>
                    <p className="text-sm opacity-90">
                      {isItalian
                        ? `Identità decentralizzata di ${petData.name}`
                        : `${petData.name}'s Decentralized Identity`}
                    </p>
                  </div>
                </div>
                <button
                  onClick={onClose}
                  className="text-white/80 hover:text-white transition-colors cursor-pointer"
                >
                  <X className="w-6 h-6" />
                </button>
              </div>
            </div>

            {/* Current DID Info */}
            {petData.did && (
              <div className="p-4 bg-purple-50 dark:bg-purple-900/20 border-b border-purple-100 dark:border-purple-800/30">
                <div className="text-sm text-purple-600 dark:text-purple-400 mb-1">
                  {tr('Current DID', 'DID attuale')}
                </div>
                <div className="font-mono text-sm text-gray-700 dark:text-gray-300 break-all bg-white dark:bg-black/20 rounded-lg p-2 border border-purple-200 dark:border-purple-700/30">
                  {petData.did}
                </div>
                {petData.didStatus && (
                  <div
                    className={`mt-2 inline-flex items-center gap-1.5 px-2 py-1 rounded-full text-xs font-medium ${
                      petData.didStatus === 'active'
                        ? 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400'
                        : 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400'
                    }`}
                  >
                    <span
                      className={`w-1.5 h-1.5 rounded-full ${petData.didStatus === 'active' ? 'bg-green-500' : 'bg-red-500'}`}
                    />
                    {petData.didStatus === 'active'
                      ? tr('Active', 'Attivo')
                      : tr('Revoked', 'Revocato')}
                  </div>
                )}
              </div>
            )}

            {/* Menu Options */}
            <div className="p-6 space-y-3">
              {/* Manage Permissions */}
              <button
                onClick={() => setCurrentView('permissions')}
                className="w-full flex items-center justify-between p-4 bg-gradient-to-r from-blue-50 to-indigo-50 dark:from-blue-900/20 dark:to-indigo-900/20 border border-blue-200 dark:border-blue-700/30 rounded-xl hover:shadow-md transition-all group"
              >
                <div className="flex items-center gap-4">
                  <div className="w-12 h-12 bg-gradient-to-br from-blue-500 to-indigo-500 rounded-xl flex items-center justify-center">
                    <Settings className="w-6 h-6 text-white" />
                  </div>
                  <div className="text-left">
                    <div className="font-semibold text-gray-900 dark:text-white">
                      {tr('Manage Permissions', 'Gestisci permessi')}
                    </div>
                    <div className="text-sm text-gray-600 dark:text-gray-400">
                      {tr(
                        'Control who can view and update records',
                        'Controlla chi può visualizzare e aggiornare i record'
                      )}
                    </div>
                  </div>
                </div>
                <ChevronRight className="w-5 h-5 text-gray-400 group-hover:translate-x-1 transition-transform" />
              </button>
            </div>
          </div>
        </div>
      </>
    );
  }

  // Permissions View
  if (currentView === 'permissions') {
    return (
      <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
        <div className="bg-white dark:bg-[#16151D] rounded-xl shadow-2xl max-w-4xl w-full max-h-[90vh] overflow-hidden flex flex-col">
          {/* Header */}
          <div className="bg-gradient-to-r from-blue-600 to-indigo-600 text-white p-6">
            <div className="flex justify-between items-center">
              <div className="flex items-center gap-3">
                <Settings className="w-8 h-8" />
                <div>
                  <h2 className="text-xl font-semibold">
                    {tr('DID Permissions', 'Permessi DID')}
                  </h2>
                  <p className="text-sm opacity-90">
                    {isItalian
                      ? `Configura i controlli di accesso per ${petData.name}`
                      : `Configure access controls for ${petData.name}`}
                  </p>
                </div>
              </div>
              <button
                onClick={onClose}
                className="text-white/80 hover:text-white transition-colors"
              >
                <X className="w-6 h-6" />
              </button>
            </div>
          </div>

          {/* Content */}
          <div className="flex-1 overflow-auto p-6 space-y-6">
            {/* Who Can View Data */}
            <div className="bg-white dark:bg-[#1E1D26] border border-gray-200 dark:border-gray-700 rounded-lg p-6">
              <h4 className="font-semibold text-gray-900 dark:text-white mb-4 flex items-center gap-2">
                <Shield className="w-5 h-5 text-blue-600" />
                {tr('Who can view data?', 'Chi può vedere i dati?')}
              </h4>
              <div className="space-y-2">
                {professionalRoles.map((role) => (
                  <label
                    key={role.id}
                    className="flex items-center gap-3 p-3 hover:bg-gray-50 dark:hover:bg-gray-800 rounded-lg cursor-pointer"
                  >
                    <input
                      type="checkbox"
                      checked={permissions.viewData.includes(role.id)}
                      onChange={() => togglePermission('viewData', role.id)}
                      className="w-5 h-5 text-purple-600 rounded"
                    />
                    <span className="text-xl">{role.icon}</span>
                    <span className="text-gray-900 dark:text-white">
                      {role.label}
                    </span>
                  </label>
                ))}
              </div>
            </div>

            {/* Who Can Amend Fields */}
            <div className="bg-white dark:bg-[#1E1D26] border border-gray-200 dark:border-gray-700 rounded-lg p-6">
              <h4 className="font-semibold text-gray-900 dark:text-white mb-4 flex items-center gap-2">
                <Key className="w-5 h-5 text-orange-600" />
                {tr(
                  'Who can amend specific fields?',
                  'Chi può modificare campi specifici?'
                )}
              </h4>
              <p className="text-sm text-gray-600 dark:text-gray-400 mb-4">
                {tr(
                  'These roles can update certain information (e.g., vets can update medical records)',
                  'Questi ruoli possono aggiornare alcune informazioni (es. i veterinari possono aggiornare le cartelle mediche)'
                )}
              </p>
              <div className="space-y-2">
                {professionalRoles.map((role) => (
                  <label
                    key={role.id}
                    className="flex items-center gap-3 p-3 hover:bg-gray-50 dark:hover:bg-gray-800 rounded-lg cursor-pointer"
                  >
                    <input
                      type="checkbox"
                      checked={permissions.amendFields.includes(role.id)}
                      onChange={() => togglePermission('amendFields', role.id)}
                      className="w-5 h-5 text-purple-600 rounded"
                    />
                    <span className="text-xl">{role.icon}</span>
                    <span className="text-gray-900 dark:text-white">
                      {role.label}
                    </span>
                  </label>
                ))}
              </div>
            </div>

            {/* Who Can Issue Credentials */}
            <div className="bg-white dark:bg-[#1E1D26] border border-gray-200 dark:border-gray-700 rounded-lg p-6">
              <h4 className="font-semibold text-gray-900 dark:text-white mb-4 flex items-center gap-2">
                <CheckCircle className="w-5 h-5 text-green-600" />
                {tr(
                  'Who can issue verifiable credentials?',
                  'Chi può emettere credenziali verificabili?'
                )}
              </h4>
              <p className="text-sm text-gray-600 dark:text-gray-400 mb-4">
                {tr(
                  'These roles can issue blockchain-verified certificates (vaccination records, training certificates, etc.)',
                  'Questi ruoli possono emettere certificati verificati su blockchain (vaccinazioni, certificati di addestramento, ecc.)'
                )}
              </p>
              <div className="space-y-2">
                {professionalRoles.map((role) => (
                  <label
                    key={role.id}
                    className="flex items-center gap-3 p-3 hover:bg-gray-50 dark:hover:bg-gray-800 rounded-lg cursor-pointer"
                  >
                    <input
                      type="checkbox"
                      checked={permissions.issueCredentials.includes(role.id)}
                      onChange={() =>
                        togglePermission('issueCredentials', role.id)
                      }
                      className="w-5 h-5 text-purple-600 rounded"
                    />
                    <span className="text-xl">{role.icon}</span>
                    <span className="text-gray-900 dark:text-white">
                      {role.label}
                    </span>
                  </label>
                ))}
              </div>
            </div>

            {/* Time-Bound Permissions */}
            <div className="bg-white dark:bg-[#1E1D26] border border-gray-200 dark:border-gray-700 rounded-lg p-6">
              <h4 className="font-semibold text-gray-900 dark:text-white mb-4">
                {tr(
                  'Time-Bound Permissions (Optional)',
                  'Permessi a tempo (opzionale)'
                )}
              </h4>
              <label className="flex items-start gap-3 cursor-pointer mb-4">
                <input
                  type="checkbox"
                  checked={permissions.timeBound}
                  onChange={(e) =>
                    setPermissions((prev) => ({
                      ...prev,
                      timeBound: e.target.checked,
                    }))
                  }
                  className="w-5 h-5 text-purple-600 rounded mt-0.5"
                />
                <div>
                  <div className="font-medium text-gray-900 dark:text-white">
                    {tr(
                      'Set expiry date for all permissions',
                      'Imposta una data di scadenza per tutti i permessi'
                    )}
                  </div>
                  <div className="text-sm text-gray-600 dark:text-gray-400">
                    {tr(
                      'Automatically revoke all professional access after a specific date',
                      'Revoca automaticamente tutti gli accessi professionali dopo una data specifica'
                    )}
                  </div>
                </div>
              </label>

              {permissions.timeBound && (
                <div className="ml-8">
                  <label className="block text-sm text-gray-600 dark:text-gray-400 mb-2">
                    {tr('Expiry Date', 'Data di scadenza')}
                  </label>
                  <input
                    type="date"
                    value={permissions.expiryDate}
                    onChange={(e) =>
                      setPermissions((prev) => ({
                        ...prev,
                        expiryDate: e.target.value,
                      }))
                    }
                    className="px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent bg-white dark:bg-[#0D0C12] text-gray-900 dark:text-white"
                  />
                </div>
              )}
            </div>
          </div>

          {/* Footer */}
          <div className="border-t border-gray-200 dark:border-gray-700 p-6 bg-gray-50 dark:bg-[#0D0C12] flex justify-between">
            <button
              onClick={() => setCurrentView('menu')}
              disabled={isSaving}
              className="px-6 py-3 border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors font-medium disabled:opacity-50"
            >
              {tr('Back', 'Indietro')}
            </button>
            <button
              onClick={handleSavePermissions}
              disabled={isSaving || saveSuccess}
              className={`px-8 py-3 text-white rounded-lg transition-all font-medium flex items-center gap-2 disabled:opacity-70 ${
                saveSuccess
                  ? 'bg-gradient-to-r from-green-600 to-emerald-600'
                  : 'bg-gradient-to-r from-blue-600 to-indigo-600 hover:opacity-90'
              }`}
            >
              {isSaving ? (
                <>
                  <Loader className="w-5 h-5 animate-spin" />
                  {tr('Saving...', 'Salvataggio...')}
                </>
              ) : saveSuccess ? (
                <>
                  <CheckCircle className="w-5 h-5" />
                  {tr('Saved!', 'Salvato!')}
                </>
              ) : (
                <>
                  <CheckCircle className="w-5 h-5" />
                  {tr('Save Permissions', 'Salva permessi')}
                </>
              )}
            </button>
          </div>
        </div>
      </div>
    );
  }

  return null;
}
